import { Controller } from '@hotwired/stimulus'
import Dashboard from '@uppy/dashboard'

import { uppyS3StorageInstance, uppyLocalStorageInstance, uploadedS3FileData, uploadedLocalFileData } from '../src/mixins/uppy'

// Drives the batch upload page, which exists alongside the standard one rather
// than replacing it: same Uppy front end, but records are created in batches.
//
// Files land in S3 one at a time, and their records are created one POST per
// BATCH_SIZE files instead of one POST per file. A batch is also flushed
// FLUSH_DELAY_MS after the last upload finishes, so a partial batch at the end
// of a run - or a slow trickle of large files - is never left hanging.
const BATCH_SIZE = 200
const FLUSH_DELAY_MS = 750

// A batch that never reaches the server means files sitting in S3 with no
// record in Curo, so transient failures are retried rather than logged. Safe
// to retry blind: a lost response is indistinguishable from a lost request, so
// the server treats the S3 object key as an idempotency key and skips anything
// the flight already has (see Flights::CreateDeviceMediasForm).
const MAX_ATTEMPTS = 4
const RETRY_BASE_DELAY_MS = 1000
// Caps the locally calculated backoff only. A delay the server asked for is
// honoured in full - capping that would retry inside the window it wants us to
// wait out - up to MAX_RETRY_WAIT_MS, beyond which the batch fails visibly
// rather than being held indefinitely.
const MAX_BACKOFF_DELAY_MS = 30000
const MAX_RETRY_WAIT_MS = 300000
const RETRIABLE_STATUSES = [408, 425, 429]

export default class extends Controller {
  static targets = ['result', 'uploadButton', 'dashboard']
  static values = {
    server: Boolean
  }

  connect () {
    this.pending = []
    this.inFlight = new Set()
    this.flushTimer = null
    this.uppy = this.createUppy()
  }

  disconnect () {
    this.cancelFlushTimer()

    // Best effort: the request may not outlive the page, but leaving the batch
    // buffered guarantees it never arrives.
    this.flush()

    if (this.uppy) {
      this.uppy.destroy()
      this.uppy = null
    }
  }

  createUppy () {
    const uppyId = this.dashboardTarget.id
    let uppy

    if (this.serverValue) {
      uppy = uppyS3StorageInstance(uppyId)
        .on('upload-success', (file, response) => {
          // Store the uploaded file data
          const fileData = uploadedS3FileData(file, response)
          this.enqueueFileData(file, fileData)
        })
    } else {
      uppy = uppyLocalStorageInstance(uppyId)
        .on('upload-success', (file, response) => {
          // Store the uploaded file data
          const fileData = uploadedLocalFileData(file, response)
          this.enqueueFileData(file, fileData)
        })
    }

    // Allowed file types for media uploads:
    // - Image: jpg, jpeg, dng (drone camera formats), tiff (professional)
    // - Video: mp4, mov (common drone/robot video formats)
    // - .NAV: satellite positioning data
    // - .OBS: raw GNSS observation logs
    // - .BIN: flight log that stores low-level system data
    // - .MRK: timestamp and position (GPS co-ordinates) for each photo captured
    // - .DAT: These are deep-dive logs that track everything the drone does in real-time
    // - .RTK: RTK data files used for high-precision positioning
    uppy.setOptions({
      restrictions: {
        allowedFileTypes: ['image/jpg', 'image/jpeg', 'image/dng', 'image/tiff', 'video/mp4', 'video/quicktime', '.nav', '.obs', '.bin', '.mrk', '.dat', '.rtk']
      }
    })

    uppy.use(Dashboard, {
      target: '#' + uppyId,
      inline: true,
      height: 550,
      width: '100%',
      showProgressDetails: true,
      note: 'Drag and drop your flight media files here, or click to browse. Supports images (jpg, jpeg, dng, tiff), videos (mp4, mov), and drone data files (.nav, .obs, .bin, .mrk, .dat, .rtk).',
      proudlyDisplayPoweredByUppy: false
    })

    uppy.on('upload-error', (file, error) => {
      console.error('Upload error:', error)
    })

    // Getting a file into S3 is only half the job - the record is created by the
    // batch POST that follows. Registering that as a post-processor is what
    // makes Uppy wait for it, so `complete` and the dashboard mean the media is
    // actually in Curo. Without it the run reports success while batches are
    // still in flight, and a user who leaves at that point strands them in S3
    // with nothing to show for it.
    uppy.addPostProcessor(() => this.settle())

    return uppy
  }

  // fileData example:
  // '{"id":"f3a7432e4c0592f0d2d16970bd24b1a7.png","storage":"cache","metadata":{"size":1801768,"filename":"DJI_001.png","mime_type":"image/png"}}'
  enqueueFileData (file, fileData) {
    this.pending.push({ file, fileData })

    if (this.pending.length >= BATCH_SIZE) {
      this.flush()
    } else {
      this.scheduleFlush()
    }
  }

  scheduleFlush () {
    this.cancelFlushTimer()
    this.flushTimer = setTimeout(() => this.flush(), FLUSH_DELAY_MS)
  }

  cancelFlushTimer () {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
  }

  flush () {
    this.cancelFlushTimer()

    while (this.pending.length > 0) {
      this.track(this.submitBatch(this.pending.splice(0, BATCH_SIZE)))
    }
  }

  track (promise) {
    this.inFlight.add(promise)
    promise.finally(() => this.inFlight.delete(promise)).catch(() => {})
  }

  // Sends whatever is still buffered and waits for every batch already on its
  // way, retry timers included. submitBatch never rejects - it reports failure
  // through the dashboard - so this settles either way.
  async settle () {
    this.flush()

    while (this.inFlight.size > 0) {
      await Promise.allSettled([...this.inFlight])
    }
  }

  async submitBatch (batch, attempt = 1) {
    const csrfToken = this.element.querySelector('input[name="authenticity_token"]')?.value
    const actionUrl = this.element.getAttribute('action')
    const formData = new FormData()
    // Keyed by index so each file stays at its position in the batch, which is how the server reports the files it rejects.
    batch.forEach(({ fileData }, index) => {
      const { id, storage, metadata = {} } = JSON.parse(fileData)
      const key = `device_media[files][${index}]`
      formData.append(`${key}[id]`, id)
      formData.append(`${key}[storage]`, storage)
      formData.append(`${key}[metadata][size]`, metadata.size ?? '')
      formData.append(`${key}[metadata][filename]`, metadata.filename ?? '')
      formData.append(`${key}[metadata][mime_type]`, metadata.mime_type ?? '')
    })

    try {
      const response = await fetch(actionUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json'
        }
      })

      if (response.ok) {
        const body = await response.json().catch(() => ({}))
        this.markRejectedFiles(batch, body.failed)
        return
      }

      if (this.isRetriable(response.status) && attempt < MAX_ATTEMPTS) {
        const requested = this.retryAfterMs(response.headers.get('Retry-After'))

        if (requested !== null && requested > MAX_RETRY_WAIT_MS) {
          this.failBatch(batch, `the server asked to retry after ${Math.round(requested / 1000)}s`)
          return
        }

        await this.wait(requested ?? this.backoffDelay(attempt))
        return this.submitBatch(batch, attempt + 1)
      }

      this.failBatch(batch, `the server responded ${response.status}`)
    } catch (error) {
      if (attempt < MAX_ATTEMPTS) {
        await this.wait(this.backoffDelay(attempt))
        return this.submitBatch(batch, attempt + 1)
      }

      this.failBatch(batch, error.message)
    }
  }

  isRetriable (status) {
    return status >= 500 || RETRIABLE_STATUSES.includes(status)
  }

  backoffDelay (attempt) {
    return Math.min(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), MAX_BACKOFF_DELAY_MS)
  }

  // Retry-After is either delay-seconds or an HTTP-date; honour both, so a
  // server that answers with a date is not retried ahead of the time it asked
  // for. Returns null when the header is absent or unparseable.
  retryAfterMs (header) {
    if (!header) return null

    if (/^\d+$/.test(header.trim())) {
      const seconds = Number.parseInt(header, 10)
      return seconds > 0 ? seconds * 1000 : null
    }

    const until = Date.parse(header)
    if (Number.isNaN(until)) return null

    const delay = until - Date.now()
    return delay > 0 ? delay : null
  }

  wait (ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  // Files the server took the batch for but could not turn into a record
  // (unsupported type, missing metadata, ...). Those are per-file problems, so
  // only the files named in the response are marked.
  markRejectedFiles (batch, failed) {
    if (!Array.isArray(failed) || failed.length === 0) return

    failed.forEach(({ index, errors }) => {
      const entry = batch[index]
      if (!entry) return

      this.markFailed(entry.file, (errors || []).join(', ') || 'was rejected')
    })

    this.announce(`${failed.length} file(s) could not be added to this flight. See the file list for details.`)
  }

  // The whole batch never made it. Without this the files show as uploaded in
  // the dashboard while existing nowhere in Curo.
  failBatch (batch, reason) {
    batch.forEach(({ file }) => this.markFailed(file, reason))

    this.announce(`${batch.length} file(s) reached S3 but could not be recorded: ${reason}. Please retry them.`)
  }

  // Marks the file the way Uppy's own error handler does. Deliberately not
  // `uppy.emit('upload-error', ...)`: that raises one toast per file, and a
  // batch is up to BATCH_SIZE of them. The batch gets a single message instead.
  markFailed (file, reason) {
    if (!this.uppy || !file || !this.uppy.getFile(file.id)) return

    this.uppy.setFileState(file.id, { error: `Could not create the media record: ${reason}` })
  }

  announce (message) {
    this.uppy?.info(message, 'error', 10000)
  }
}
